import {
  useEffect,
  useMemo,
  useState,
} from "react";
import { CalculatorIcon } from "lucide-react";

export default function SlopeCalculator({
  course,
}) {
  const teesWithRatings = useMemo(
    () =>
      (course?.tees || []).filter(
        (tee) =>
          (tee.ratings || []).length >
          0
      ),
    [course]
  );

  const [
    handicapInput,
    setHandicapInput,
  ] = useState("");

  const [
    selectedTeeId,
    setSelectedTeeId,
  ] = useState("");

  const [
    selectedCategory,
    setSelectedCategory,
  ] = useState("");

  useEffect(() => {
    if (
      teesWithRatings.length === 0
    ) {
      setSelectedTeeId("");
      setSelectedCategory("");
      return;
    }

    const selectedStillExists =
      teesWithRatings.some(
        (tee) =>
          tee.id === selectedTeeId
      );

    if (!selectedStillExists) {
      setSelectedTeeId(
        teesWithRatings[0].id
      );
    }
  }, [
    teesWithRatings,
    selectedTeeId,
  ]);

  const selectedTee =
    teesWithRatings.find(
      (tee) =>
        tee.id === selectedTeeId
    ) ||
    teesWithRatings[0] ||
    null;

  const availableRatings =
    selectedTee?.ratings || [];

  useEffect(() => {
    if (
      availableRatings.length === 0
    ) {
      setSelectedCategory("");
      return;
    }

    const categoryStillExists =
      availableRatings.some(
        (rating) =>
          rating.rating_category ===
          selectedCategory
      );

    if (!categoryStillExists) {
      setSelectedCategory(
        availableRatings[0]
          .rating_category
      );
    }
  }, [
    availableRatings,
    selectedCategory,
  ]);

  const selectedRating =
    availableRatings.find(
      (rating) =>
        rating.rating_category ===
        selectedCategory
    ) ||
    availableRatings[0] ||
    null;

  const handicapIndex =
    parseHandicapIndex(
      handicapInput
    );

  const par =
    selectedTee &&
    selectedRating
      ? getRatingPar(
          course,
          selectedTee,
          selectedRating
        )
      : null;

  const calculation =
    calculatePlayingHandicap({
      handicapIndex,
      rating:
        selectedRating,
      par,
    });

  if (
    teesWithRatings.length === 0
  ) {
    return (
      <div className="rounded-2xl border border-[#dfcfab] bg-[#fbf5e7] px-4 py-4 text-sm text-[#8a6623]">
        <div className="flex items-start gap-3">
          <CalculatorIcon className="mt-0.5 h-5 w-5 shrink-0" />

          <div>
            <p className="font-semibold">
              Slopekalkylator
            </p>

            <p className="mt-1 text-xs leading-5">
              Banan saknar
              registrerad Course
              Rating/Slope och kan
              därför inte beräkna
              spelhandicap.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const hasMultipleCategories =
    availableRatings.length > 1;

  return (
    <section className="overflow-hidden rounded-2xl border border-[#c7d6cb] bg-white shadow-sm">
      <div className="border-b border-[#dce6df] bg-[#edf5ef] px-4 py-4 sm:px-5">
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#183d2d] text-white">
            <CalculatorIcon className="h-5 w-5" />
          </span>

          <div>
            <h3 className="text-sm font-semibold text-[#183d2d]">
              Slopekalkylator
            </h3>

            <p className="mt-1 max-w-2xl text-xs leading-5 text-[#5f6f66]">
              Ange ditt exakta
              handicap och välj tee
              för att se ditt
              spelhandicap (SPHC).
            </p>
          </div>
        </div>
      </div>

      <div className="p-4 sm:p-5">
        <div className="grid gap-4 lg:grid-cols-[repeat(3,minmax(0,1fr))_minmax(180px,0.78fr)]">
          <CalculatorField
            label="Exakt handicap"
          >
            <input
              type="text"
              inputMode="decimal"
              value={
                handicapInput
              }
              onChange={(
                event
              ) =>
                setHandicapInput(
                  event.target.value
                )
              }
              placeholder="Ex. 18,4"
              className="h-12 w-full rounded-xl border border-[#c9c1b2] bg-white px-3 text-sm font-semibold text-[#183d2d] outline-none transition placeholder:font-normal placeholder:text-[#a0a59f] focus:border-[#183d2d] focus:ring-2 focus:ring-[#183d2d]/10"
            />
          </CalculatorField>

          <CalculatorField
            label="Tee"
          >
            <select
              value={
                selectedTee?.id ||
                ""
              }
              onChange={(
                event
              ) =>
                setSelectedTeeId(
                  event.target.value
                )
              }
              className="h-12 w-full rounded-xl border border-[#c9c1b2] bg-white px-3 text-sm font-semibold text-[#183d2d] outline-none transition focus:border-[#183d2d] focus:ring-2 focus:ring-[#183d2d]/10"
            >
              {teesWithRatings.map(
                (tee) => (
                  <option
                    key={tee.id}
                    value={tee.id}
                  >
                    {tee.name}
                  </option>
                )
              )}
            </select>
          </CalculatorField>

          <CalculatorField
            label="Kategori"
          >
            <select
              value={
                selectedRating
                  ?.rating_category ||
                ""
              }
              onChange={(
                event
              ) =>
                setSelectedCategory(
                  event.target.value
                )
              }
              disabled={
                !hasMultipleCategories
              }
              className="h-12 w-full rounded-xl border border-[#c9c1b2] bg-white px-3 text-sm font-semibold text-[#183d2d] outline-none transition focus:border-[#183d2d] focus:ring-2 focus:ring-[#183d2d]/10 disabled:bg-[#f4f1ea] disabled:text-[#7b817d]"
            >
              {availableRatings.map(
                (rating) => (
                  <option
                    key={`${selectedTee?.id}-${rating.rating_category}`}
                    value={
                      rating.rating_category
                    }
                  >
                    {getRatingCategoryLabel(
                      rating.rating_category
                    )}
                  </option>
                )
              )}
            </select>
          </CalculatorField>

          <CalculatorField
            label="Spelhandicap"
          >
            <div className="flex h-12 items-center justify-between gap-3 rounded-xl bg-[#183d2d] px-4 text-white shadow-[0_8px_20px_rgba(24,61,45,0.14)]">
              <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[#d7c28e]">
                SPHC
              </span>

              <strong className="text-2xl font-semibold tracking-tight">
                {calculation.error
                  ? "–"
                  : calculation.value ===
                    null
                  ? "–"
                  : formatPlayingHandicap(
                      calculation.value
                    )}
              </strong>
            </div>
          </CalculatorField>
        </div>

        <div className="mt-4 rounded-xl border border-[#ece7dc] bg-[#faf8f2] px-4 py-3">
          {calculation.error ? (
            <p className="text-xs font-medium leading-5 text-[#a0443e]">
              {calculation.error}
            </p>
          ) : (
            <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
              <p className="text-[11px] leading-5 text-[#7b817d]">
                Plus-handicap kan
                skrivas exempelvis{" "}
                <strong className="text-[#183d2d]">
                  +2,1
                </strong>
                .
              </p>

              {selectedRating && (
                <div className="flex flex-wrap gap-x-5 gap-y-1 text-[11px] text-[#68716c]">
                  <span>
                    CR{" "}
                    <strong className="text-[#183d2d]">
                      {formatNumber(
                        selectedRating.course_rating
                      )}
                    </strong>
                  </span>

                  <span>
                    Slope{" "}
                    <strong className="text-[#183d2d]">
                      {selectedRating.slope_rating}
                    </strong>
                  </span>

                  <span>
                    Par{" "}
                    <strong className="text-[#183d2d]">
                      {par ?? "–"}
                    </strong>
                  </span>

                  <span className="font-medium text-[#183d2d]">
                    {Number(
                      selectedRating.hole_count
                    ) === 9
                      ? "9-hålsberäkning"
                      : "18-hålsberäkning"}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function CalculatorField({
  label,
  children,
}) {
  return (
    <label className="block min-w-0">
      <span className="mb-1.5 block text-[10px] font-semibold uppercase tracking-[0.14em] text-[#7b817d]">
        {label}
      </span>

      {children}
    </label>
  );
}

function parseHandicapIndex(
  value
) {
  const raw = String(
    value ?? ""
  ).trim();

  if (!raw) {
    return null;
  }

  const normalized =
    raw.replace(",", ".");

  if (
    normalized.startsWith(
      "+"
    )
  ) {
    const numberValue =
      Number(
        normalized.slice(1)
      );

    return Number.isFinite(
      numberValue
    )
      ? -Math.abs(
          numberValue
        )
      : NaN;
  }

  const numberValue =
    Number(normalized);

  return Number.isFinite(
    numberValue
  )
    ? numberValue
    : NaN;
}

function calculatePlayingHandicap({
  handicapIndex,
  rating,
  par,
}) {
  if (
    handicapIndex === null
  ) {
    return {
      value: null,
      error: "",
    };
  }

  if (
    !Number.isFinite(
      handicapIndex
    )
  ) {
    return {
      value: null,
      error:
        "Ange ett giltigt handicap, till exempel 18,4 eller +2,1.",
    };
  }

  if (
    handicapIndex > 54
  ) {
    return {
      value: null,
      error:
        "Exakt handicap kan som högst vara 54,0.",
    };
  }

  if (
    !rating ||
    !Number.isFinite(
      Number(
        rating.course_rating
      )
    ) ||
    !Number.isFinite(
      Number(
        rating.slope_rating
      )
    ) ||
    !Number.isFinite(
      Number(par)
    )
  ) {
    return {
      value: null,
      error:
        "Banan saknar komplett ratingdata för det valda alternativet.",
    };
  }

  const isNineHole =
    Number(
      rating.hole_count
    ) === 9;

  const adjustedIndex =
    isNineHole
      ? roundToTenth(
          handicapIndex /
            2
        )
      : handicapIndex;

  const rawCourseHandicap =
    adjustedIndex *
      (Number(
        rating.slope_rating
      ) /
        113) +
    (Number(
      rating.course_rating
    ) -
      Number(par));

  return {
    value:
      Math.floor(
        rawCourseHandicap +
          0.5
      ),
    error: "",
  };
}

function roundToTenth(
  value
) {
  return (
    Math.floor(
      value * 10 +
        0.5
    ) / 10
  );
}

function formatPlayingHandicap(
  value
) {
  return value < 0
    ? `+${Math.abs(
        value
      )}`
    : String(value);
}

function getRatingCategoryLabel(
  category
) {
  return category === "women"
    ? "Dam"
    : category === "men"
    ? "Herr"
    : category;
}

function getRatingPar(
  course,
  tee,
  rating
) {
  const startHole =
    Number(
      rating.start_hole
    );

  const holeCount =
    Number(
      rating.hole_count
    );

  const relevantHoles =
    (course?.holes || [])
      .filter((hole) => {
        const number =
          Number(
            hole.hole_number
          );

        return (
          number >=
            startHole &&
          number <
            startHole +
              holeCount
        );
      })
      .sort(
        (a, b) =>
          Number(
            a.hole_number
          ) -
          Number(
            b.hole_number
          )
      );

  if (
    relevantHoles.length !==
    holeCount
  ) {
    return null;
  }

  const handicapRows =
    (tee.handicaps || []).filter(
      (row) =>
        row.rating_category ===
        rating.rating_category
    );

  const rowByHoleId =
    new Map(
      handicapRows.map(
        (row) => [
          row.hole_id,
          row,
        ]
      )
    );

  let total = 0;

  for (
    const hole of
      relevantHoles
  ) {
    const row =
      rowByHoleId.get(
        hole.id
      );

    if (
      !row ||
      !Number.isFinite(
        Number(row.par)
      )
    ) {
      return null;
    }

    total += Number(
      row.par
    );
  }

  return total;
}

function formatNumber(value) {
  if (
    value === null ||
    value === undefined ||
    value === ""
  ) {
    return "–";
  }

  return String(value).replace(
    ".",
    ","
  );
}